/**
 * SuccessState — confirmation post-submit (Chunk 9.N.2 v2).
 *
 * Affichée après POST 201 `intake_event_id` opaque. La référence est exposée
 * pour permettre au client final de l'envoyer au courtier en cas de question
 * — c'est un UUID v4, ne fuit aucune info tenant/project/link (déjà validé
 * 9.N.1.B2).
 *
 * Parité visuelle stricte avec `<InvalidState>` : même chrome, même pastille
 * h-14 w-14, même typo. Seul le glyphe ✓ emerald + le texte + la carte
 * référence + le postscript changent.
 *
 * Bouton « Copier » :
 *   - `navigator.clipboard.writeText` si dispo (HTTPS context — production VPS).
 *   - Fallback silencieux : pas d'erreur, pas de toast d'échec — l'utilisateur
 *     peut toujours sélectionner manuellement le UUID. C'est un nice-to-have,
 *     pas un blocker.
 *   - Reset du libellé "Copié" après 1500ms.
 *
 * Pas de bouton "Retour" — le client final ferme la page, le courtier prend
 * le relais (sous-onglet « Soumissions publiques » 9.N.3 future).
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

export type SuccessStateProps = {
  readonly intakeEventId: string;
  readonly kind?: "form" | "documents";
};

export function SuccessState({
  intakeEventId,
  kind = "documents",
}: SuccessStateProps): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(intakeEventId);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      }
      // Fallback silencieux si clipboard API absente — l'utilisateur peut
      // toujours sélectionner manuellement le UUID dans le <code>.
    } catch {
      // Idem — pas de surface d'erreur (privacy + simplicity).
    }
  };

  return (
    <div className="motion-safe:animate-fade-in rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] sm:p-10">
      <div
        aria-hidden="true"
        className="motion-safe:animate-scale-in mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 ring-8 ring-emerald-50/40"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className="h-7 w-7 text-emerald-600"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.5 12.5l4 4 9-9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h1 className="mt-5 text-2xl font-semibold tracking-tight text-slate-900 sm:text-[26px]">
        {t(`public_intake.states.success.${kind === "form" ? "form_title" : "title"}`)}
      </h1>
      <p className="mx-auto mt-2 max-w-sm text-pretty text-sm text-slate-600">
        {t("public_intake.states.success.body")}
      </p>

      <div
        role="status"
        aria-live="polite"
        className="mt-5 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-3 text-left"
      >
        <span className="block text-[11px] font-medium uppercase tracking-wide text-slate-500">
          {t("public_intake.states.success.ref_label")}
        </span>
        <div className="mt-1 flex items-start gap-2">
          <code
            translate="no"
            className="block min-w-0 flex-1 break-all font-mono text-xs text-slate-800"
          >
            {intakeEventId}
          </code>
          <button
            type="button"
            onClick={() => void handleCopy()}
            aria-label={t("public_intake.states.success.copy_button_aria")}
            className="focus-visible:ring-brand-500 inline-flex flex-shrink-0 touch-manipulation items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {copied
              ? t("public_intake.states.success.copied_toast")
              : t("public_intake.states.success.copy_button")}
          </button>
        </div>
      </div>

      <p className="mx-auto mt-5 max-w-sm text-xs text-slate-500">
        {t("public_intake.states.success.postscript")}
      </p>
    </div>
  );
}
